/** Shared period / date / month / multi-ID filters for lists and reports. */

/** App timezone for “today / this week / this month” and fiscal boundaries (matches API). */
export const APP_TIMEZONE = 'Asia/Karachi'

/**
 * Period query values.
 * Relative: today | week | month (no `year`).
 * Fiscal (July–June): q1–q4 | h1 | h2 | year — `year` = fiscal START calendar year.
 */
export type DocumentPeriod =
  | 'today'
  | 'week'
  | 'month'
  | 'q1'
  | 'q2'
  | 'q3'
  | 'q4'
  | 'h1'
  | 'h2'
  | 'year'

export type RelativeDocumentPeriod = 'today' | 'week' | 'month'
export type FiscalDocumentPeriod = 'q1' | 'q2' | 'q3' | 'q4' | 'h1' | 'h2' | 'year'

export type AmountMode = 'normal' | 'withGst'

export interface DocumentDateFilters {
  period?: DocumentPeriod
  /** Fiscal start year (YYYY) for q1–q4 / h1 / h2 / year. Ignored for today/week/month. */
  year?: number | string
  dateFrom?: string
  dateTo?: string
  /** Calendar months as YYYY-MM. Ignored by API when period is today|week|month. */
  months?: string[]
}

export interface MultiIdFilters {
  vendorIds?: string[]
  partyIds?: string[]
  agentIds?: string[]
  categoryIds?: string[]
  designIds?: string[]
  qualityIds?: string[]
  colorIds?: string[]
  /** Singular aliases still accepted by the API. */
  vendorId?: string
  partyId?: string
  agentId?: string
  categoryId?: string
  designId?: string
  qualityId?: string
  colorId?: string
}

export interface ReportListFilterParams
  extends DocumentDateFilters,
    MultiIdFilters {
  amountMode?: AmountMode
  status?: string
}

export function isRelativePeriod(
  period: string | null | undefined,
): period is RelativeDocumentPeriod {
  return (
    period === 'today' || period === 'week' || period === 'month'
  )
}

export function isFiscalPeriod(
  period: string | null | undefined,
): period is FiscalDocumentPeriod {
  return (
    period === 'q1' ||
    period === 'q2' ||
    period === 'q3' ||
    period === 'q4' ||
    period === 'h1' ||
    period === 'h2' ||
    period === 'year'
  )
}

/** Karachi calendar parts (month 1–12). */
export function getAppTimezoneParts(date = new Date()): {
  year: number
  month: number
  day: number
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date)

  const year = Number(parts.find((p) => p.type === 'year')?.value)
  const month = Number(parts.find((p) => p.type === 'month')?.value)
  const day = Number(parts.find((p) => p.type === 'day')?.value)
  return { year, month, day }
}

/**
 * Fiscal year start (July). If month >= Jul → this calendar year, else previous.
 * year=2025 ⇒ 2025-26 (2025-07-01 … 2026-06-30).
 */
export function currentFiscalStartYear(date = new Date()): number {
  const { year, month } = getAppTimezoneParts(date)
  return month >= 7 ? year : year - 1
}

/** Display label for fiscal start year, e.g. 2025 → "2025/26". */
export function formatFiscalYearLabel(startYear: number | string): string {
  const y = Number(startYear)
  if (!Number.isFinite(y)) return String(startYear)
  const end = String(y + 1).slice(-2)
  return `${y}/${end}`
}

/** Fiscal start-year select options (newest first). */
export function buildYearOptions(count = 8): Array<{
  value: string
  label: string
}> {
  const current = currentFiscalStartYear()
  return Array.from({ length: count }, (_, i) => {
    const start = current - i
    return { value: String(start), label: formatFiscalYearLabel(start) }
  })
}

export const DOCUMENT_PERIOD_OPTIONS: Array<{
  value: DocumentPeriod | 'none'
  label: string
}> = [
  { value: 'none', label: 'All dates' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week (Mon–today)' },
  { value: 'month', label: 'This month (month-to-date)' },
  { value: 'q1', label: 'Q1 (Jul–Sep)' },
  { value: 'q2', label: 'Q2 (Oct–Dec)' },
  { value: 'q3', label: 'Q3 (Jan–Mar)' },
  { value: 'q4', label: 'Q4 (Apr–Jun)' },
  { value: 'h1', label: 'H1 (Jul–Dec)' },
  { value: 'h2', label: 'H2 (Jan–Jun)' },
  { value: 'year', label: 'Full fiscal year (Jul–Jun)' },
]

export function getDocumentPeriodLabel(
  period: DocumentPeriod | 'none' | null | undefined,
): string {
  if (!period || period === 'none') return 'All dates'
  return (
    DOCUMENT_PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period
  )
}

/**
 * Append period/year/dateFrom/dateTo.
 * Custom dates override period. Do not send year for today/week/month.
 * Months are independent (intersect on server) except API ignores them for relative periods.
 */
export function appendDateFilters(
  query: Record<string, string | number>,
  filters: DocumentDateFilters,
): void {
  const dateFrom = filters.dateFrom?.trim()
  const dateTo = filters.dateTo?.trim()

  if (dateFrom || dateTo) {
    if (dateFrom) query.dateFrom = dateFrom
    if (dateTo) query.dateTo = dateTo
  } else if (filters.period) {
    query.period = filters.period
    if (isFiscalPeriod(filters.period)) {
      const yearRaw = filters.year
      if (yearRaw != null && String(yearRaw).trim() !== '') {
        const year = Number(yearRaw)
        if (Number.isFinite(year)) query.year = year
      }
    }
  }

  if (!isRelativePeriod(filters.period)) {
    appendCsvParam(query, 'months', filters.months)
  }
}

export function appendCsvParam(
  query: Record<string, string | number>,
  key: string,
  values?: string[] | null,
): void {
  if (!values?.length) return
  const cleaned = values.map((v) => String(v).trim()).filter(Boolean)
  if (!cleaned.length) return
  query[key] = cleaned.join(',')
}

/** Stable order for multi-select / month arrays used in TanStack Query keys. */
export function sortedStringList(values?: string[] | null): string[] | undefined {
  if (!values?.length) return undefined
  const cleaned = values.map((v) => String(v).trim()).filter(Boolean)
  if (!cleaned.length) return undefined
  return cleaned.length > 1 ? [...cleaned].sort() : cleaned
}

/** Copy params with sorted list fields so equivalent filters share one query cache entry. */
export function withSortedListFields<T extends object>(
  params: T,
  keys: readonly (keyof T)[],
): T {
  const next = { ...params }
  for (const key of keys) {
    const value = next[key]
    if (!Array.isArray(value)) continue
    const sorted = sortedStringList(value as string[])
    if (sorted) next[key] = sorted as T[keyof T]
    else delete next[key]
  }
  return next
}

function coalesceIds(
  plural?: string[],
  singular?: string,
): string[] | undefined {
  if (plural?.length) return plural
  if (singular?.trim()) return [singular.trim()]
  return undefined
}

/** Append plural ID filters (CSV). Prefers *Ids; falls back to singular *Id. */
export function appendMultiIdFilters(
  query: Record<string, string | number>,
  filters: MultiIdFilters,
): void {
  appendCsvParam(query, 'vendorIds', coalesceIds(filters.vendorIds, filters.vendorId))
  appendCsvParam(query, 'partyIds', coalesceIds(filters.partyIds, filters.partyId))
  appendCsvParam(query, 'agentIds', coalesceIds(filters.agentIds, filters.agentId))
  appendCsvParam(
    query,
    'categoryIds',
    coalesceIds(filters.categoryIds, filters.categoryId),
  )
  appendCsvParam(query, 'designIds', coalesceIds(filters.designIds, filters.designId))
  appendCsvParam(query, 'qualityIds', coalesceIds(filters.qualityIds, filters.qualityId))
  appendCsvParam(query, 'colorIds', coalesceIds(filters.colorIds, filters.colorId))
}

export function appendAmountMode(
  query: Record<string, string | number>,
  amountMode?: AmountMode,
): void {
  if (!amountMode) return
  query.amountMode = amountMode
}

/** Recent calendar months as YYYY-MM options (newest first). */
export function buildRecentMonthOptions(count = 24): Array<{
  value: string
  label: string
}> {
  const now = new Date()
  const options: Array<{ value: string; label: string }> = []
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1))
    const yyyy = d.getUTCFullYear()
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const value = `${yyyy}-${mm}`
    const label = d.toLocaleString('en-US', {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
    options.push({ value, label })
  }
  return options
}
